/* AgentMeeting — Auth pages (signin / signup / magic-link verify) */

import React from 'react';
import { MarketingBar } from '../shared/marketing-bar';
import { auth as authApi, HTTPError, type User } from '../shared/api';
import { useAuth } from '../shared/auth';

export function Auth({ goTo, mode }) {
  // mode: 'signin' | 'signup' | 'verify'
  const [m, setM] = React.useState(mode || 'signin');
  React.useEffect(() => { if (mode) setM(mode); }, [mode]);

  if (m === 'verify') return <VerifyMagicLink goTo={goTo} setMode={setM} />;
  return <AuthForm mode={m} setMode={setM} goTo={goTo} />;
}

function AuthForm({ mode, setMode, goTo }) {
  const isSignup = mode === 'signup';
  const { signIn } = useAuth();
  const [email, setEmail] = React.useState('');
  const [pw, setPw] = React.useState('');
  const [name, setName] = React.useState('');
  const [agreed, setAgreed] = React.useState(false);
  const [pwMode, setPwMode] = React.useState('password'); // magic | password
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [devToken, setDevToken] = React.useState<string | null>(null);
  const [errMsg, setErrMsg] = React.useState<string | null>(null);

  const submit = async (e) => {
    e?.preventDefault();
    setErrMsg(null);
    setSubmitting(true);
    try {
      if (isSignup) {
        if (pwMode === 'magic') {
          // Sign-up via magic mode: backend auto-creates account & returns token
          const res = await authApi.signup({ email, name });
          if ('token' in res && 'user' in res) {
            signIn(res.token, res.user as User);
            // Hard navigate — bypasses React state batching that can
            // otherwise trigger the /app/* route-guard before useAuth's
            // setUser commits. window.location.replace forces a full
            // reload at the destination so the AuthProvider initializes
            // fresh with the persisted token.
            window.location.replace('/#/app/meetings');
          }
        } else {
          // Password sign-up
          const res = await authApi.signup({ email, password: pw, name });
          signIn(res.token, res.user);
          window.location.replace('/#/app/meetings');
        }
      } else {
        // Sign-in
        if (pwMode === 'magic') {
          const res = await authApi.signin({ email, use_magic: true });
          if ('sent' in res && res.sent) {
            setSent(true);
            setDevToken(res.dev_token || null);
          } else if ('token' in res && 'user' in res) {
            // Some backends may return token directly even in magic mode
            signIn(res.token, res.user as User);
            window.location.replace('/#/app/meetings');
          }
        } else {
          const res = await authApi.signin({ email, password: pw });
          if ('token' in res && 'user' in res) {
            signIn(res.token, res.user as User);
            window.location.replace('/#/app/meetings');
          }
        }
      }
    } catch (err) {
      if (err instanceof HTTPError) {
        setErrMsg(err.body?.error || 'Something went wrong');
      } else {
        setErrMsg('Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const useDevToken = async () => {
    if (!devToken) return;
    setErrMsg(null);
    setSubmitting(true);
    try {
      const res = await authApi.verifyMagic(devToken);
      signIn(res.token, res.user);
      window.location.replace('/#/app/meetings');
    } catch (err) {
      if (err instanceof HTTPError) {
        setErrMsg(err.body?.error || 'Something went wrong');
      } else {
        setErrMsg('Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-0)', color: 'var(--fg-0)',
    }}>
      <MarketingBar here={null} goTo={goTo} />

      <div className="grow" style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 0,
      }}>
        {/* Left: form */}
        <div style={{
          padding: '48px 48px', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', maxWidth: 520, marginLeft: 'auto', width: '100%',
        }}>
          <span className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.16em' }}>
            [{isSignup ? 'SIGN UP' : 'SIGN IN'}]
          </span>
          <h1 style={{
            fontSize: 36, fontWeight: 500, letterSpacing: '-0.02em',
            margin: '12px 0 8px', lineHeight: 1.1,
          }}>
            {isSignup ? '把你的会议室开起来' : '欢迎回来。'}
          </h1>
          <p style={{ color: 'var(--fg-1)', fontSize: 14, lineHeight: 1.6, margin: '0 0 32px' }}>
            {isSignup
              ? '免费, 不要信用卡。注册即送 $5 LLM credits。'
              : '把 6 个 AI 叫到你这边继续吵。'}
          </p>

          {sent ? (
            <SentState
              email={email}
              devToken={devToken}
              submitting={submitting}
              onUseDevToken={useDevToken}
              onBack={() => { setSent(false); setEmail(''); setDevToken(null); setErrMsg(null); }}
              errMsg={errMsg}
            />
          ) : (
            <form onSubmit={submit} className="col gap-3">
              {/* SSO row */}
              <div className="col gap-2">
                <button type="button" className="btn-lg ghost mono"
                  onClick={() => goTo('app/meetings')}
                  style={{ width: '100%', justifyContent: 'flex-start' }}>
                  <span className="ssoicon" style={{
                    background: '#fff', color: '#000',
                  }}>G</span>
                  <span style={{ flex: 1, textAlign: 'center' }}>
                    continue with Google
                  </span>
                </button>
                <button type="button" className="btn-lg ghost mono"
                  onClick={() => goTo('app/meetings')}
                  style={{ width: '100%', justifyContent: 'flex-start' }}>
                  <span className="ssoicon" style={{
                    background: 'var(--fg-0)', color: 'var(--bg-0)',
                  }}>⌥</span>
                  <span style={{ flex: 1, textAlign: 'center' }}>
                    continue with GitHub
                  </span>
                </button>
                <button type="button" className="btn-lg ghost mono"
                  onClick={() => goTo('app/meetings')}
                  style={{ width: '100%', justifyContent: 'flex-start' }}>
                  <span className="ssoicon" style={{
                    background: '#000', color: '#fff', border: '1px solid var(--line-3)',
                  }}>SSO</span>
                  <span style={{ flex: 1, textAlign: 'center' }}>
                    SAML / Workspace SSO
                  </span>
                </button>
              </div>

              {/* divider */}
              <div className="flex center" style={{
                gap: 12, color: 'var(--fg-3)',
                fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.1em',
              }}>
                <div style={{ flex: 1, height: 1, background: 'var(--line-1)' }} />
                <span>OR WITH EMAIL</span>
                <div style={{ flex: 1, height: 1, background: 'var(--line-1)' }} />
              </div>

              {isSignup && (
                <div className="col gap-1">
                  <label className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>NAME</label>
                  <input className="input"
                    value={name} onChange={e => setName(e.target.value)}
                    placeholder="你叫啥都行 · 真名 / 花名 / 神名"
                    required />
                </div>
              )}

              <div className="col gap-1">
                <label className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>EMAIL</label>
                <input className="input" type="email"
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@somewhere.com" required autoFocus />
              </div>

              {/* password vs magic-link toggle */}
              <div style={{
                display: 'inline-flex', padding: 3,
                border: '1px solid var(--line-2)', borderRadius: 5,
                background: 'var(--bg-1)', alignSelf: 'flex-start',
              }}>
                {[['magic', 'magic link', '📨'], ['password', 'password', '⌨']].map(([id, lbl, ic]) => (
                  <button key={id} type="button" onClick={() => setPwMode(id)}
                    style={{
                      padding: '6px 12px', borderRadius: 3,
                      background: pwMode === id ? 'var(--bg-3)' : 'transparent',
                      color: pwMode === id ? 'var(--fg-0)' : 'var(--fg-2)',
                      border: 'none', cursor: 'pointer',
                      fontFamily: 'var(--mono)', fontSize: 11,
                    }}>
                    <span style={{ marginRight: 6 }}>{ic}</span>{lbl}
                  </button>
                ))}
              </div>

              {pwMode === 'password' && (
                <div className="col gap-1">
                  <div className="flex between">
                    <label className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>PASSWORD</label>
                    {!isSignup && (
                      <a className="mono tiny" style={{ color: 'var(--live)', cursor: 'pointer' }}
                        onClick={() => alert('demo · forgot password 流程')}>
                        forgot?
                      </a>
                    )}
                  </div>
                  <input className="input" type="password"
                    value={pw} onChange={e => setPw(e.target.value)}
                    placeholder={isSignup ? '至少 12 位, 别用 12345678' : '••••••••'}
                    required />
                  {isSignup && pw && (
                    <PwStrength pw={pw} />
                  )}
                </div>
              )}
              {pwMode === 'magic' && (
                <div className="meta" style={{
                  padding: 12, background: 'var(--bg-1)',
                  border: '1px dashed var(--line-2)', borderRadius: 4,
                  fontSize: 12, lineHeight: 1.5,
                }}>
                  <span style={{ color: 'var(--live)' }}>✓</span> 不用记密码。
                  我们把一条一次性链接发到你邮箱 ·
                  你点一下就登进来 · 链接 15 分钟过期。
                </div>
              )}

              {isSignup && (
                <label className="flex gap-2" style={{ alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input type="checkbox" checked={agreed}
                    onChange={e => setAgreed(e.target.checked)} required
                    style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.5 }}>
                    我同意 <a className="link" onClick={(e) => e.preventDefault()}>服务条款</a> 与 <a className="link" onClick={(e) => e.preventDefault()}>隐私政策</a> ·
                    我也同意收 <span className="dim">很少</span> 的产品更新邮件 (你可以随时退订)。
                  </span>
                </label>
              )}

              <button type="submit" className="btn-lg primary mono"
                disabled={submitting || (isSignup && !agreed)}
                style={{ width: '100%', marginTop: 4 }}>
                {submitting
                  ? '$ submitting…'
                  : pwMode === 'magic'
                    ? `$ send magic link →`
                    : isSignup ? '$ create account →' : '$ sign in →'}
              </button>

              {errMsg && (
                <div className="mono tiny" style={{
                  color: 'var(--err)', marginTop: 4, fontSize: 12,
                  padding: '8px 10px', background: 'var(--bg-1)',
                  border: '1px solid var(--err)', borderRadius: 4,
                }}>
                  ! {errMsg}
                </div>
              )}

              <div className="meta" style={{ textAlign: 'center', fontSize: 12 }}>
                {isSignup ? (
                  <>已有账号? <a className="link" onClick={() => setMode('signin')}>sign in</a></>
                ) : (
                  <>还没有账号? <a className="link" onClick={() => setMode('signup')}>sign up free</a></>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Right: terminal preview / pitch */}
        <div className="auth-side" style={{
          background: 'var(--bg-1)', borderLeft: '1px solid var(--line-1)',
          padding: '48px 48px', display: 'flex', flexDirection: 'column',
          maxWidth: 600, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle at 80% 20%, var(--live-dim) 0%, transparent 60%)',
            opacity: 0.4, pointerEvents: 'none',
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <span className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.16em' }}>
              [WHAT YOU GET]
            </span>
            <h2 style={{
              fontSize: 24, fontWeight: 500, letterSpacing: '-0.02em',
              margin: '12px 0 24px', lineHeight: 1.3,
            }}>
              一个能被 6 个 AI<br />同时拷打的工作台。
            </h2>

            <div className="col gap-3" style={{ marginBottom: 32 }}>
              {[
                ['$5', 'LLM credits 免费送, 大约够开 5 ~ 10 场会'],
                ['6', '内置 agent 模板 · CEO / CFO / 用户代表 / 红队 / ...'],
                ['1500', 'min/月 会议时长 · Pro 套餐'],
                ['0%', 'token 加价 · 透明转嫁 LLM 成本'],
              ].map(([n, t]) => (
                <div key={n} className="flex gap-3" style={{ alignItems: 'flex-start' }}>
                  <span className="mono" style={{
                    fontSize: 22, fontWeight: 500, color: 'var(--live)',
                    minWidth: 56, lineHeight: 1,
                  }}>{n}</span>
                  <span style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.5 }}>{t}</span>
                </div>
              ))}
            </div>

            {/* mini terminal */}
            <div style={{
              padding: 16, background: 'var(--bg-0)',
              border: '1px solid var(--line-2)', borderRadius: 6,
              fontFamily: 'var(--mono)', fontSize: 11, lineHeight: 1.7,
            }}>
              <div style={{ color: 'var(--fg-3)' }}>// after sign up</div>
              <div><span style={{ color: 'var(--live)' }}>$</span> agentmeeting init</div>
              <div className="dim">→ choose pack: <span style={{ color: 'var(--fg-0)' }}>red-blue-team</span></div>
              <div className="dim">→ enter motion: <span style={{ color: 'var(--fg-0)' }}>"Q3 进巴西市场?"</span></div>
              <div style={{ color: 'var(--live)' }}>✓ 4 agents loaded · agenda generated</div>
              <div style={{ marginTop: 8 }}>
                <span style={{ color: 'var(--live)' }}>$</span> agentmeeting start
              </div>
              <div className="dim" style={{ animation: 'blink 1.4s steps(2) infinite' }}>
                ▶ meeting in progress...
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{
        padding: '14px 32px', borderTop: '1px solid var(--line-1)',
        background: 'var(--bg-1)', textAlign: 'center',
      }}>
        <span className="mono tiny dim" style={{ letterSpacing: '0.06em' }}>
          受 SOC 2 / ISO 27001 / GDPR 保护 · 你的会议默认私有 · 2025 © agentmeeting.dev
        </span>
      </div>
    </div>
  );
}

function PwStrength({ pw }) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const labels = ['too short', 'weak', 'ok', 'good', 'strong', 'overkill'];
  const colors = ['var(--err)', 'var(--err)', 'var(--finance)', 'var(--judge)', 'var(--live)', 'var(--live)'];
  return (
    <div className="flex center gap-2" style={{ marginTop: 4 }}>
      <div className="flex gap-1 grow">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} style={{
            flex: 1, height: 3, borderRadius: 1,
            background: i < score ? colors[score] : 'var(--bg-3)',
          }} />
        ))}
      </div>
      <span className="mono tiny" style={{ color: colors[score], minWidth: 60, textAlign: 'right' }}>
        {labels[score]}
      </span>
    </div>
  );
}

function SentState({ email, devToken, submitting, onUseDevToken, onBack, errMsg }: {
  email: string;
  devToken: string | null;
  submitting: boolean;
  onUseDevToken: () => void;
  onBack: () => void;
  errMsg: string | null;
}) {
  return (
    <div className="col gap-3">
      <div style={{
        padding: 24, border: '1px solid var(--live)',
        borderRadius: 6, background: 'var(--bg-1)',
        boxShadow: '0 0 24px var(--live-dim)',
      }}>
        <div className="flex gap-3" style={{ alignItems: 'center', marginBottom: 12 }}>
          <span style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'var(--live-dim)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: 18,
          }}>📨</span>
          <div>
            <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.1em' }}>
              [LINK SENT · 200 OK]
            </div>
            <div style={{ fontSize: 16, fontWeight: 500, marginTop: 2 }}>
              我们刚发了一封信
            </div>
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.6 }}>
          收件人: <span className="mono" style={{ color: 'var(--fg-0)' }}>{email || 'you@example.com'}</span>
          <br />
          标题: <span className="dim">[AgentMeeting] 你的一次性登录链接</span>
          <br />
          失效: <span className="dim">15 分钟后</span>
        </div>
      </div>

      <div className="meta" style={{ fontSize: 12, lineHeight: 1.6 }}>
        没看见? · 检查垃圾邮件 · 或 <a className="link" onClick={onBack}>重新发送</a> · 仍然不行?
        <a className="link" onClick={onBack}> 用密码登录</a>。
      </div>

      {/* dev shortcut: real dev_token from backend */}
      {devToken && (
        <div style={{
          padding: 12, background: 'var(--bg-2)',
          border: '1px dashed var(--line-2)', borderRadius: 4,
          fontSize: 11, fontFamily: 'var(--mono)',
        }}>
          <div className="dim" style={{ marginBottom: 6 }}>// dev mode · dev_token:</div>
          <div style={{
            wordBreak: 'break-all', color: 'var(--fg-2)',
            marginBottom: 8, fontSize: 10,
          }}>{devToken}</div>
          <button type="button" className="btn-lg primary mono"
            onClick={onUseDevToken} disabled={submitting}
            style={{ width: '100%' }}>
            {submitting ? '$ verifying…' : 'Use dev token →'}
          </button>
        </div>
      )}

      {errMsg && (
        <div className="mono tiny" style={{
          color: 'var(--err)', fontSize: 12,
          padding: '8px 10px', background: 'var(--bg-1)',
          border: '1px solid var(--err)', borderRadius: 4,
        }}>
          ! {errMsg}
        </div>
      )}
    </div>
  );
}

function VerifyMagicLink({ goTo, setMode }) {
  const { signIn } = useAuth();
  const [phase, setPhase] = React.useState<'checking' | 'ok' | 'err'>('checking');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      // Parse ?token=… from the hash query string (e.g. "#auth/verify?token=abc")
      let token: string | null = null;
      try {
        const hash = location.hash || '';
        const qIdx = hash.indexOf('?');
        if (qIdx >= 0) {
          const params = new URLSearchParams(hash.slice(qIdx + 1));
          token = params.get('token');
        }
      } catch { /* noop */ }

      if (!token) {
        if (!cancelled) setPhase('err');
        return;
      }
      try {
        const res = await authApi.verifyMagic(token);
        if (cancelled) return;
        signIn(res.token, res.user);
        setPhase('ok');
        setTimeout(() => {
          if (cancelled) return;
          window.location.replace('/#/app/meetings');
        }, 600);
      } catch {
        if (!cancelled) setPhase('err');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-0)', color: 'var(--fg-0)',
    }}>
      <div style={{
        width: 480, padding: '48px 40px',
        border: '1px solid var(--line-2)', borderRadius: 8,
        background: 'var(--bg-1)', textAlign: 'center',
      }}>
        <div className="mono tiny" style={{
          color: phase === 'ok' ? 'var(--live)' : phase === 'err' ? 'var(--err)' : 'var(--judge)',
          letterSpacing: '0.16em',
        }}>
          [{phase === 'ok' ? 'AUTHENTICATED' : phase === 'err' ? 'LINK EXPIRED' : 'VERIFYING TOKEN'}]
        </div>

        <div style={{
          margin: '32px auto', width: 80, height: 80, borderRadius: '50%',
          border: '2px solid ' + (phase === 'ok' ? 'var(--live)' : phase === 'err' ? 'var(--err)' : 'var(--judge)'),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 32,
          boxShadow: '0 0 24px ' + (phase === 'ok' ? 'var(--live-dim)' : 'transparent'),
          animation: phase === 'checking' ? 'spin 1.4s linear infinite' : 'none',
        }}>
          {phase === 'ok' ? '✓' : phase === 'err' ? '✕' : '⟳'}
        </div>

        <h2 style={{ fontSize: 22, fontWeight: 500, margin: '0 0 8px', letterSpacing: '-0.02em' }}>
          {phase === 'ok' ? '欢迎进入。' : phase === 'err' ? 'Link expired or invalid' : '正在验证你的链接...'}
        </h2>
        <p className="meta" style={{ margin: 0 }}>
          {phase === 'ok'
            ? '正在跳转到工作台 · 1s'
            : phase === 'err'
              ? '该链接无效或已过期 · 请重新申请'
              : '校验 token · 检查时效 · 配置 session'}
        </p>

        {phase === 'err' && (
          <button type="button" className="btn-lg primary mono"
            onClick={() => { setMode && setMode('signin'); goTo('signin'); }}
            style={{ marginTop: 24, width: '100%' }}>
            $ Try again →
          </button>
        )}

        <div style={{
          marginTop: 32, padding: 12,
          background: 'var(--bg-0)', borderRadius: 4,
          fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--fg-2)',
          textAlign: 'left', lineHeight: 1.7,
        }}>
          <div><span className="dim">$</span> validate_token(...)</div>
          <div className="dim">[1] expires_at &lt; now()? <span style={{
            color: phase === 'err' ? 'var(--err)' : 'var(--live)',
          }}>{phase === 'err' ? 'FAIL' : 'OK'}</span></div>
          <div className="dim">[2] single_use? <span style={{
            color: phase === 'err' ? 'var(--err)' : 'var(--live)',
          }}>{phase === 'err' ? 'FAIL' : 'OK'}</span></div>
          <div className="dim">[3] ip_match? <span style={{
            color: phase === 'err' ? 'var(--err)' : 'var(--live)',
          }}>{phase === 'err' ? 'FAIL' : 'OK'}</span></div>
          {phase === 'ok' && (
            <div style={{ color: 'var(--live)' }}>→ session created · redirecting</div>
          )}
          {phase === 'err' && (
            <div style={{ color: 'var(--err)' }}>→ verification failed</div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
